import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { User, LogOut, ChevronDown, Shield, Music, Bell, Headphones, FileText, X, Megaphone } from "lucide-react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const API = `/api`;

async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.register('/sw-push.js');
    const { data } = await axios.get(`${API}/push/vapid-key`);
    if (!data.public_key) return;

    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
    };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key)
      });
    }
    await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() });
  } catch (e) {
    console.log('Push subscription skipped:', e.message);
  }
}

export const Header = () => {
  const { user, logout, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (user) subscribeToPush();
  }, [user]);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setNotifications(data);
    } catch {}
  }, []);

  const handleOpenNotifs = () => {
    setNotifOpen(!notifOpen);
    setMenuOpen(false);
    if (!notifOpen) fetchNotifs();
  };

  const handleMarkSeen = async () => {
    await axios.post(`${API}/auth/mark-notifications-seen`);
    setNotifOpen(false);
    setNotifications([]);
    fetchUser();
  };

  return (
    <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE7E0] sticky top-0 z-40" data-testid="app-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 bg-[#6B8576] rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
            <Music className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <span className="font-semibold text-[#2C3531] hidden sm:block" style={{ fontFamily: 'Outfit' }}>Audio Sur Mesure</span>
        </Link>

        <div className="flex items-center gap-2">
          {/* Notification bell */}
          <div className="relative">
            <button onClick={handleOpenNotifs} className="relative p-2 rounded-xl hover:bg-[#FAF9F6] transition-colors duration-200" data-testid="notification-bell">
              <Bell className="w-5 h-5 text-[#6A7771]" strokeWidth={1.5} />
              {user?.notifications > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#D9A892] rounded-full text-[10px] text-white flex items-center justify-center font-bold" data-testid="notification-badge">
                  {user.notifications > 9 ? '9+' : user.notifications}
                </span>
              )}
            </button>

            <AnimatePresence>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-12 bg-white rounded-3xl shadow-lg border border-[#EBE7E0] w-80 z-50 overflow-hidden"
                    data-testid="notification-panel"
                  >
                    <div className="p-4 border-b border-[#EBE7E0] flex items-center justify-between">
                      <h3 className="font-semibold text-[#2C3531] text-sm" style={{ fontFamily: 'Outfit' }}>Notifications</h3>
                      <button onClick={() => setNotifOpen(false)} className="p-1 rounded-lg hover:bg-[#FAF9F6]">
                        <X className="w-4 h-4 text-[#6A7771]" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-[#6A7771] text-sm">
                          Aucune nouvelle notification
                        </div>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id} className="px-4 py-3 border-b border-[#EBE7E0]/50 hover:bg-[#FAF9F6] transition-colors duration-200">
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${n.type === 'audio' ? 'bg-[#6B8576]/10' : n.type === 'admin' ? 'bg-[#6B8576]/10' : 'bg-[#D9A892]/10'}`}>
                                {n.type === 'audio' ? <Headphones className="w-4 h-4 text-[#6B8576]" strokeWidth={1.5} /> : n.type === 'admin' ? <Megaphone className="w-4 h-4 text-[#6B8576]" strokeWidth={1.5} /> : <FileText className="w-4 h-4 text-[#D9A892]" strokeWidth={1.5} />}
                              </div>
                              <div>
                                <p className="text-xs font-medium text-[#6A7771]">{n.title}</p>
                                <p className="text-sm text-[#2C3531]">{n.message}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="p-3 border-t border-[#EBE7E0]">
                        <button onClick={handleMarkSeen} className="w-full py-2 text-sm text-[#6B8576] font-medium hover:bg-[#FAF9F6] rounded-xl transition-colors duration-200" data-testid="mark-seen-btn">
                          Tout marquer comme lu
                        </button>
                      </div>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* User menu */}
          <div className="relative">
            <button onClick={() => { setMenuOpen(!menuOpen); setNotifOpen(false); }} className="flex items-center gap-2 px-3 py-1.5 rounded-2xl hover:bg-[#FAF9F6] transition-all duration-300" data-testid="user-menu-button">
              <div className="w-8 h-8 bg-[#E4D1C3] rounded-xl flex items-center justify-center">
                <User className="w-4 h-4 text-[#6B8576]" strokeWidth={1.5} />
              </div>
              <span className="text-sm text-[#2C3531] hidden sm:block font-medium">{user?.name}</span>
              <ChevronDown className="w-4 h-4 text-[#6A7771]" strokeWidth={1.5} />
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-12 bg-white rounded-2xl shadow-lg border border-[#EBE7E0] py-2 w-52 z-50">
                  {user?.role === 'admin' && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} className="w-full px-4 py-2.5 text-left text-[#2C3531] hover:bg-[#FAF9F6] flex items-center gap-2.5 transition-colors duration-200" data-testid="admin-link">
                      <Shield className="w-4 h-4 text-[#6B8576]" strokeWidth={1.5} /> Administration
                    </Link>
                  )}
                  <button onClick={async () => { setMenuOpen(false); await logout(); window.location.href = '/'; }} className="w-full px-4 py-2.5 text-left text-[#B86666] hover:bg-red-50/50 flex items-center gap-2.5 transition-colors duration-200" data-testid="logout-button">
                    <LogOut className="w-4 h-4" strokeWidth={1.5} /> Déconnexion
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
